<template>
  <div class="reports">
    <!-- Campaign Selection -->
    <div class="form-group">
      <el-select v-model="selectedCampaign" placeholder="Select a campaign" @change="fetchReport" class="select-field">
        <el-option v-for="campaign in campaigns.data" :key="campaign.campaign_id" :label="campaign.name"
          :value="campaign.campaign_id" />
      </el-select>
      <el-date-picker v-model="selectedDateRange" type="daterange" placeholder="Select a date range" format="yyyy-MM-dd"
        :value-format="'yyyy-MM-dd'" @change="fetchReport" class="date-picker" style="width: 30%;" />
      <el-input v-model="searchTerm" placeholder="Search campaigns, labels, and assets" class="search-input"
        style="width: 30%;" />
    </div>

    <div v-if="selectedCampaignData" class="campaign-details">
      <p><strong>Campaign Name:</strong> {{ selectedCampaignData.name }}</p>
      <p><strong>Start Date:</strong> {{ selectedCampaignData.start_date }}</p>
      <p><strong>Description:</strong> {{ selectedCampaignData.description }}</p>
      <p><strong>Label Share (%):</strong> {{ selectedCampaignData.label_share }}</p>
      <p><strong>Platform Share (%):</strong> {{ selectedCampaignData.platform_share }}</p>
    </div>

    <div class="reports">
      <!-- Label Campaign Report -->
      <div>
        <div style="display: flex; justify-content: space-between;">
          <h2>Label Campaign Report</h2>
          <el-button @click="exportToCSV('label')">Export</el-button>
        </div>

        <el-table v-if="labelReportData.length > 0" :data="filteredLabelReportData" class="label-report-table"
          style="width: 100%">
          <el-table-column label="Label Name" prop="label_name" />
          <el-table-column label="Total Revenue (USD)" prop="total_revenue" />
          <el-table-column label="Label Revenue (USD)" prop="label_revenue" />
          <el-table-column label="Label Share (%)" prop="label_share" />
          <el-table-column label="Channel Revenue (USD)" prop="channel_revenue" />
          <el-table-column label="Platform Revenue (USD)" prop="platform_revenue" />
        </el-table>
        <!-- No label report available message -->
        <el-table v-else class="label-report-table" style="width: 100%">
          <el-table-column label="Label Name" />
          <el-table-column label="Total Revenue (USD)" />
          <el-table-column label="Label Revenue (USD)" />
          <el-table-column label="Label Share (%)" />
          <el-table-column label="Channel Revenue (USD)" />
          <el-table-column label="Platform Revenue (USD)" />
          <el-table-column>
            <template #default>
              <div class="no-data-message">No label report available for the selected campaign.</div>
            </template>
          </el-table-column>
        </el-table>
      </div>

      <!-- Assets Report -->
      <div>
        <div style="display: flex; justify-content: space-between;">
          <h2>Asset Campaign Report</h2>
          <el-button @click="exportToCSV('asset')">Export</el-button>
        </div>
        <el-table v-if="assetsReportsData.length > 0" :data="filteredAssetsReportsData" class="assets-report-table"
          style="width: 100%">
          <el-table-column label="Asset ID" prop="asset_id" />
          <el-table-column label="Track Title" prop="track_title" />
          <el-table-column label="Track Label" prop="track_label" />
          <el-table-column label="Total Revenue Share (USD)" prop="total_rev_share_revenue" />
        </el-table>
        <!-- No assets report available message -->
        <el-table v-else class="assets-report-table" style="width: 100%">
          <el-table-column label="Asset ID" />
          <el-table-column label="Track Title" />
          <el-table-column label="Track Label" />
          <el-table-column label="Total Revenue Share (USD)" />
          <el-table-column>
            <template #default>
              <div class="no-data-message">No asset report available for the selected campaign.</div>
            </template>
          </el-table-column>
        </el-table>
      </div>

      <!-- Channels Report -->
      <div>
        <div style="display: flex; justify-content: space-between;">
          <h2>Channel Campaign Report</h2>
          <el-button @click="exportToCSV('channel')">Export</el-button>
        </div>
        <el-table v-if="channelsData.length > 0" :data="filteredChannelsData" class="channels-report-table"
          style="width: 100%">
          <el-table-column label="Channel ID" prop="channel_id" />
          <el-table-column label="Channel Title" prop="channel_title" />
          <el-table-column label="Label Name" prop="label_name" />
          <el-table-column label="Asset Title" prop="asset_title" />
          <el-table-column label="Total Revenue (USD)" prop="total_revenue" />
          <el-table-column label="Label Revenue (USD)" prop="label_revenue" />
          <el-table-column label="Channel Revenue (USD)" prop="channel_revenue" />
          <el-table-column label="Platform Revenue (USD)" prop="platform_revenue" />
        </el-table>
        <!-- No channel report available message -->
        <el-table v-else class="channels-report-table" style="width: 100%">
          <el-table-column label="Channel ID" />
          <el-table-column label="Channel Title" />
          <el-table-column label="Label Name" />
          <el-table-column label="Asset Title" />
          <el-table-column label="Total Revenue (USD)" />
          <el-table-column label="Label Revenue (USD)" />
          <el-table-column label="Channel Revenue (USD)" />
          <el-table-column label="Platform Revenue (USD)" />
          <el-table-column>
            <template #default>
              <div class="no-data-message">No channel report available for the selected campaign.</div>
            </template>
          </el-table-column>
        </el-table>
      </div>
    </div>


  </div>
</template>


<script>
import { mapState, mapActions, mapMutations } from 'vuex';

export default {
  data() {
    return {
      selectedCampaign: null,
      campaignData: [],
      labelReportData: [],
      assetsReportsData: [],
      channelsData: [],
      selectedDate: this.getCurrentMonth(),
      selectedCampaignData: null,
      searchTerm: "",
      selectedDateRange: [],
      selectedStartDate: this.getCurrentMonth().start,
      selectedEndDate: this.getCurrentMonth().end,
    };
  },

  computed: {
    ...mapState('revShareCampaigns', ['campaigns', 'loading', 'campaignReportData']),
    selectedCampaignName() {
      const campaign = this.campaignData.find(c => c.campaign_id === this.selectedCampaign);
      return campaign ? campaign.name : 'N/A';
    },
    // Filtered Label Report Data
    filteredLabelReportData() {
      const term = this.searchTerm.toLowerCase();
      return this.labelReportData.filter(item =>
        item.label_name.toLowerCase().includes(term) ||
        (item.total_revenue_usd && item.total_revenue_usd.toString().includes(term)) ||
        (item.label_revenue_usd && item.label_revenue_usd.toString().includes(term)) ||
        (item.label_share && item.label_share.toString().includes(term))
      );
    },

    // Filtered Assets Report Data
    filteredAssetsReportsData() {
      const term = this.searchTerm.toLowerCase();
      return this.assetsReportsData.filter(item =>
        (item.asset_id && item.asset_id.toString().toLowerCase().includes(term)) ||
        (item.track_title && item.track_title.toLowerCase().includes(term)) ||
        (item.track_label && item.track_label.toLowerCase().includes(term)) ||
        (item.total_rev_share_revenue && item.total_rev_share_revenue.toString().includes(term)) ||
        (item.total_gross_revenue && item.total_gross_revenue.toString().includes(term))
      );
    },

    // Filtered Channels Report Data
    filteredChannelsData() {
      const term = this.searchTerm.toLowerCase();
      return this.channelsData.filter(item =>
        (item.channel_id && item.channel_id.toString().toLowerCase().includes(term)) ||
        (item.channel_title && item.channel_title.toLowerCase().includes(term)) ||
        (item.label_name && item.label_name.toLowerCase().includes(term)) ||
        (item.asset_title && item.asset_title.toLowerCase().includes(term)) ||
        (item.total_revenue && item.total_revenue.toString().includes(term)) ||
        (item.channel_revenue && item.channel_revenue.toString().includes(term))
      );
    }
  },

  methods: {
    ...mapMutations('revShareCampaigns', ['setCampaignModal']),
    ...mapActions('revShareCampaigns', ['readCampaigns', 'fetchReportByCampaign']),

    // This will be called when a campaign is selected from the dropdown
    async fetchReport() {
      if (this.selectedCampaign && this.selectedDate) {
        this.selectedCampaignData = this.campaignData.find(campaign => campaign.campaign_id === this.selectedCampaign);

        this.labelReportData = [];
        this.assetsReportsData = [];
        this.channelsData = [];
        const [rawStartDate, rawEndDate] = this.selectedDateRange;

        // Create new Date objects based on the selected range
        const startDate = new Date(rawStartDate);
        const endDate = new Date(rawEndDate);

        try {
          // Fetch campaign report by campaign ID
          await this.fetchReportByCampaign({
            campaignId: this.selectedCampaign,
            startDate: this.formatDate(startDate),
            endDate: this.formatDate(endDate)
          });

          // Check if label data exists
          if (this.campaignReportData && this.campaignReportData.data && this.campaignReportData.data.labels) {
            this.labelReportData = Object.values(this.campaignReportData.data.labels);
          } else {
            this.labelReportData = [];
          }

          // Check if asset data exists and update
          if (this.campaignReportData && this.campaignReportData.data && Array.isArray(this.campaignReportData.data.assets)) {
            this.assetsReportsData = this.campaignReportData.data.assets;
          } else {
            this.assetsReportsData = [];
          }

          if (this.campaignReportData && this.campaignReportData.data && Array.isArray(this.campaignReportData.data.channels)) {
            this.channelsData = this.campaignReportData.data.channels;
          } else {
            this.channelsData = [];
          }
        } catch (error) {
          console.error('Error fetching report data:', error);
          this.labelReportData = [];
          this.assetsReportsData = [];
          this.channelsData = [];
        }
      }
    },

    getCurrentMonth() {
      const today = new Date();
      const start = new Date(today.getFullYear(), today.getMonth(), 1); // First day of the current month
      const end = new Date(today.getFullYear(), today.getMonth() + 1, 0); // Last day of the current month

      return {
        start: start,
        end: end
      };
    },
    formatDate(date) {
      const year = date.getFullYear();
      const month = (date.getMonth() + 1).toString().padStart(2, '0');  // Ensure 2-digit month
      const day = date.getDate().toString().padStart(2, '0');  // Ensure 2-digit day
      return `${year}-${month}-${day}`;
    },


    exportToCSV(type) {
      let data;
      switch (type) {
        case 'label':
          data = this.filteredLabelReportData;
          break;
        case 'asset':
          data = this.filteredAssetsReportsData;
          break;
        case 'channel':
          data = this.filteredChannelsData;
          break;
        default:
          return;
      }
      const headers = Object.keys(data[0] || {});
      let csvContent = headers.join(',') + '\n';
      data.forEach(row => {
        csvContent += headers.map(header => row[header] || '').join(',') + '\n';
      });

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${type}_report.csv`;
      a.click();
      URL.revokeObjectURL(url);
    },
  },

  watch: {
    campaignReportData(newData) {
      if (newData && newData.data) {
        // Update label report data based on the new campaign report data
        if (newData.data.labels) {
          this.labelReportData = Object.values(newData.data.labels);
        }

        // Update assets report data based on the new campaign report data
        if (Array.isArray(newData.data.assets)) {
          this.assetsReportsData = newData.data.assets;
        }

        // Update channels report data based on the new campaign report data
        if (Array.isArray(newData.data.channels)) {
          this.channelsData = newData.data.channels;
        }
      }
    },

    selectedCampaign(newVal) {
      // Reset labelReportData and assetsReportsData when a new campaign is selected
      this.labelReportData = [];
      this.assetsReportsData = [];
    },
  },

  async created() {
    const { start, end } = this.getCurrentMonth();
    this.selectedDateRange = [start, end];
    await this.readCampaigns(); // Ensure campaigns are loaded first
    if (this.campaigns && this.campaigns.data) {
      this.campaignData = this.campaigns.data;
      if (this.selectedCampaign) {
        this.selectedCampaignData = this.campaignData.find(campaign => campaign.campaign_id === this.selectedCampaign);

      }
    } else {
      console.error('Campaigns data not available');
    }
    this.setCampaignModal(false);
  },
};
</script>

<style scoped>
.reports {
  padding: 20px;
}

/* Input Field Styles */
.form-group {
  margin-bottom: 20px;
  display: flex;
}

.select-field,
.date-picker {
  width: 100%;
  border-radius: 6px;
  border: 1px solid #ddd;
  padding: 10px;
  font-size: 14px;
  background-color: #fff;
}

.select-field:focus,
.date-picker:focus {
  border-color: #66afe9;
  outline: none;
}

/* Label Campaign Report Table Styles */
.label-report-table {
  margin-bottom: 30px;
  border: 1px solid #ddd;
  background-color: #f9f9f9;
}

.no-data-message {
  text-align: center;
  padding: 20px;
  font-size: 16px;
  color: #999;
}

/* Campaign Info Section Styles */
.campaign-details {
  background-color: #f9f9f9;
  /* Light background */
  padding: 20px;
  border-radius: 8px;
  border: 1px solid #ddd;
  margin-bottom: 30px;
}

.campaign-details h3 {
  font-size: 1.5rem;
  margin-bottom: 15px;
  color: #333;
}

.campaign-details p {
  font-size: 14px;
  color: #555;
  margin-bottom: 10px;
}

.campaign-details p strong {
  font-weight: bold;
  color: #333;
}

.campaign-details p {
  line-height: 1.6;
}
</style>